import { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { useSnapshot } from '../../hooks/useSnapshot';
import { Card, DatePicker, Flex, Form, FormInstance, Modal, Select, Space } from 'antd';
import { ControlOutlined } from '@ant-design/icons';
import { LabelTag } from '../common/label-tag';
import { NAME_PATH, useTralitSetting } from '../../hooks/useTralitSetting';
import { AppContext } from '../../contexts/AppContext';
import { DraggableTags } from '../common/draggable-tags';

type ChartProps = {
  submit: (data) => void;
  form: FormInstance<any>;
  loading: boolean;
  drillDownRule: string[];
  setDrillDownRule: Dispatch<SetStateAction<string[]>>;
  defaultDrillDownRule: string[];
};

const ChangeHeader: React.FC<ChartProps> = (props) => {
  const { submit, form, loading, drillDownRule, setDrillDownRule, defaultDrillDownRule } = props;
  const { color } = useContext(AppContext);
  const { drillFields, setDrillFields, filter, setFilter, getAggregationPeriod } = useContext(ToshibaContext);
  const { currentSnapshot, targetSnapshotOptions, getMonthLabel } = useSnapshot();
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });

  // 下拉框数据
  const options = useMemo(
    () => ({
      snapshots: targetSnapshotOptions,
      fiscalYear: ['FY23', 'FY24', 'FY25', 'FY26', 'FY27'].map((value) => ({ key: value, label: value, value })),
      evaluationPeriod: ['今月単月', '全期', '上期', '下期', '今月まで', '実績のみ', '見込のみ'].map((value) => ({
        key: value,
        label: value,
        value,
      })),
    }),
    [targetSnapshotOptions]
  );

  // Model
  const [isSettingOpen, setSettingOpen] = useState(false);

  const [drillSaveField, setDrillSaveField] = useState([]);
  const drillLabels = {
    segment: 'セグメント',
    businessUnit: 'BU',
    model: '機種',
    customer: '顧客名',
    location: '設置場所名',
  };

  const onCloseHandler = (fieldKey) => {
    setDrillFields((pref) => pref.filter((item) => item.fieldKey !== fieldKey));
  };

  /**
   * 評価期間修改联动集計期間
   */
  const evaluationPeriodChange = (values) => {
    if (values.evaluationPeriod || values.fiscalYear) {
      const aggregationPeriod = getAggregationPeriod(
        currentSnapshot,
        form.getFieldValue('fiscalYear'),
        form.getFieldValue('evaluationPeriod')
      );
      form.setFieldValue('aggregationPeriod', aggregationPeriod);
    }
  };

  const handleOk = () => {
    form.validateFields().then(() => {
      const data = form.getFieldsValue();
      setFilter(data);
      submit(data);
      setSettingOpen(false);
      setDrillDownRule(drillSaveField);
    });
  };

  return (
    <>
      <Card styles={{ body: { padding: 0, height: '100%' } }}>
        <Flex style={{ height: '100%', alignItems: 'center' }}>
          <Flex style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <span style={{ margin: '0px 14px', fontSize: fontSize.title, fontWeight: 'bold' }}>受注変動分析</span>
            <Flex gap="4px 0" wrap style={{ flex: 1 }}>
              <LabelTag loading={loading} closable={false} fieldKey={'fiscalYear'} label={'財務年度'} value={filter.fiscalYear} />
              <LabelTag
                loading={loading}
                closable={false}
                fieldKey={'evaluationPeriod'}
                label={'評価期間'}
                value={filter.evaluationPeriod}
              />
              {drillFields.map((field, index) => {
                return (
                  <LabelTag
                    closable={drillFields.length - 1 === index}
                    key={field.fieldKey}
                    fieldKey={field.fieldKey}
                    label={drillLabels[field.fieldKey]}
                    value={field.value.join(',')}
                    onClose={() => {
                      onCloseHandler(field.fieldKey);
                    }}
                  />
                );
              })}
            </Flex>
          </Flex>
          <Space size={'large'} align="center" style={{ marginRight: 14 }} styles={{ item: { display: 'flex' } }}>
            {!loading && (
              <ControlOutlined
                style={{ cursor: 'pointer', fontSize: fontSize.tag, color: color.primaryColor }}
                onClick={() => {
                  setSettingOpen(true);
                }}
              />
            )}
          </Space>
        </Flex>
      </Card>
      <Modal
        width={750}
        title={`受注変動分析`}
        open={isSettingOpen}
        onOk={handleOk}
        onCancel={() => {
          setSettingOpen(false);
        }}
        afterOpenChange={(open) => {
          if (open) {
            setDrillSaveField(drillDownRule);
          }
        }}
      >
        <Form
          form={form}
          labelCol={{ span: 6 }}
          onValuesChange={(values) => {
            evaluationPeriodChange(values);
          }}
        >
          <Form.Item name="fiscalYear" label={'財務年度'}>
            <Select style={{ width: 120 }} options={options.fiscalYear} />
          </Form.Item>
          <Form.Item label={'評価期間'}>
            <Space>
              <Form.Item name="evaluationPeriod" noStyle>
                <Select style={{ width: 120 }} options={options.evaluationPeriod} />
              </Form.Item>
              <Form.Item name="aggregationPeriod" noStyle>
                <DatePicker.RangePicker style={{ width: 240 }} disabled />
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="targetSnapshot" label={'比較断面'}>
            <Select style={{ width: 120 }} options={options.snapshots} />
          </Form.Item>
          <Form.Item label="ドリルダウン軸">
            <DraggableTags
              defaultItems={defaultDrillDownRule.map((fieldKey) => ({
                id: fieldKey,
                text: drillLabels[fieldKey],
              }))}
              items={drillDownRule.map((fieldKey) => ({
                id: fieldKey,
                text: drillLabels[fieldKey],
              }))}
              onDragEnd={(items) => {
                setDrillSaveField(items.map((item) => item.id));
              }}
              onReset={() => {
                setDrillSaveField(defaultDrillDownRule);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ChangeHeader;
